import { debounce, isString, max, pick, snakeCase, uniq } from "lodash";
import PropTypes from "prop-types";
import React from "react";
import Badge from "react-bootstrap/Badge";
import AutosuggestMultiSectionExpandable from "../../../components/AutosuggestMultiSectionExpandable";
import StyledSubstring from "../../../components/StyledSubstring";
import { getURLGridQueryParams } from "../../../utils";
import {
  ATTACK_TACTIC_SUGGESTIONS,
  ATTACK_TECHNIQUE_SUGGESTIONS,
  getLatestKeyword,
  getLatestValue,
  getSuggestions,
  queryContainsSearchField,
  SEARCH_FIELD_SUGGESTIONS,
  SEARCH_FIELDS,
  TC_INDICATORS
} from "../mappings";
import { getFuzzySuggestions } from "../mappings/utils";
import { formatSearchQuery, isDateField, isTimeRangeKeyword } from "../util";
import { TIME_RANGE_SUGGESTIONS } from "./consts";
import { getTimerangeFromEnum } from "./utils";
import { SEARCH_QUERY_LIMIT } from "../consts";

const getSectionSuggestions = section => section.suggestions;

const renderSectionTitle = section => (
  <strong data-testid={snakeCase(`autosuggest_section_${section.title}`)}>
    {section.title}
  </strong>
);

const getSuggestionValue = suggestion => suggestion.value;

class SearchAutosuggest extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [
        {
          suggestions: SEARCH_FIELD_SUGGESTIONS,
          title: "Keywords",
          name: "keywords"
        }
      ]
    };

    this.onSuggestionsFetchRequestedDebounced = debounce(
      this.onSuggestionsFetchRequested,
      props.debounceValue
    );
  }

  onSuggestionsFetchRequested = ({ value, reason }) => {
    const { modified } = formatSearchQuery(value);
    const currentSearchContextValue = getLatestValue(modified).trim();

    let keywordSuggestions = [];
    const newState = {};

    this.props.showDatepicker(false);

    if (!currentSearchContextValue) {
      keywordSuggestions = SEARCH_FIELD_SUGGESTIONS;
    } else if (currentSearchContextValue.endsWith("__datepicker")) {
      this.props.showDatepicker(true);
    } else if (isDateField(currentSearchContextValue)) {
      keywordSuggestions = TIME_RANGE_SUGGESTIONS;
    } else if (
      currentSearchContextValue.startsWith(SEARCH_FIELDS.ATTACK_TACTIC)
    ) {
      keywordSuggestions = getFuzzySuggestions(
        currentSearchContextValue,
        ATTACK_TACTIC_SUGGESTIONS
      );
    } else if (
      currentSearchContextValue.startsWith(SEARCH_FIELDS.ATTACK_TECHNIQUE)
    ) {
      keywordSuggestions = getFuzzySuggestions(
        currentSearchContextValue,
        ATTACK_TECHNIQUE_SUGGESTIONS
      );
    } else if (currentSearchContextValue.startsWith(SEARCH_FIELDS.INDICATORS)) {
      keywordSuggestions = getFuzzySuggestions(
        currentSearchContextValue,
        TC_INDICATORS
      );
    } else {
      keywordSuggestions = getSuggestions(currentSearchContextValue);
    }

    if (keywordSuggestions.length) {
      newState.suggestions = [
        {
          suggestions: keywordSuggestions,
          title: "Keywords",
          name: "keywords"
        }
      ];
    } else {
      newState.suggestions = [];
    }

    const urlDecodedHistory = (this.props.searchHistory || [])
      .map(
        item => pick(getURLGridQueryParams(item), ["searchQuery"]).searchQuery
      )
      .filter(item => !!item);

    let historySuggestionFilter = suggestion => suggestion.includes(value);

    if (reason === "suggestion-selected") {
      historySuggestionFilter = suggestion =>
        suggestion.includes(value) &&
        suggestion.includes(this.props.searchValue);
    }

    const historySuggestions = uniq(urlDecodedHistory)
      .filter(historySuggestionFilter)
      .map(item => ({
        name: item.slice(0, SEARCH_QUERY_LIMIT),
        value: item,
        section: "history"
      }));

    if (historySuggestions.length) {
      const historySuggestionEntry = {
        suggestions: historySuggestions,
        title: "Recent Queries",
        name: "history"
      };
      if (value === "") {
        newState.suggestions.unshift(historySuggestionEntry);
      } else {
        newState.suggestions.push(historySuggestionEntry);
      }
    }
    this.setState(newState);
  };

  onSuggestionSelected = (event, { suggestionValue, suggestion }) => {
    const { searchValue } = this.props;
    if (suggestion.operator) {
      event.preventDefault();
      // Remove unnecessary space after special character: ]
      const lastValue = getLatestValue(searchValue.replace(/\s+([\]])/g, "$1"));

      this.props.onChange(event, {
        newValue: searchValue.replace(lastValue, suggestionValue),
        method: "type"
      });
      return;
    }

    if (suggestion.section === "history") {
      this.props.onChange(event, { newValue: suggestionValue, method: "type" });
      return;
    }

    let suggestionValueFixed = suggestionValue;
    if (!suggestionValue.endsWith("__datepicker")) {
      if (event.key === "Enter") {
        event.preventDefault();
        this.props.onChange(event, {
          newValue: suggestionValue,
          method: "type"
        });
      }

      // If the suggestion contains a space, but not multiple colons (which would indicate a date string),
      // surround the suggestion with quotes
      if (
        (suggestionValue.match(/:/g) || []).length <= 1 &&
        suggestionValue.indexOf("[") < 0
      ) {
        if (suggestionValue.indexOf(" ") >= 0) {
          const suggestionValueSplit = suggestionValue.split(":");
          suggestionValueFixed = `${suggestionValueSplit[0]}${
            suggestionValueSplit[1]
              ? `:${JSON.stringify(suggestionValueSplit[1])}`
              : ""
          }`;
        }
      } else if (
        (suggestionValue.match(/:/g) || []).length <= 1 &&
        suggestionValue.indexOf("[") >= 0
      ) {
        const suggestionValueSplit = suggestionValue.split("[");
        const suggestions = suggestionValueSplit[1].split(", ");
        const newSuggestions = suggestions.map(sugg => {
          const trimmedSuggestion = sugg.trim();
          return trimmedSuggestion.indexOf(" ") >= 0 &&
            trimmedSuggestion[0] !== '"'
            ? JSON.stringify(trimmedSuggestion)
            : trimmedSuggestion;
        });
        suggestionValueFixed = `${
          suggestionValueSplit[0]
        }[${newSuggestions.join(", ")}`;
      }
      this.props.showDatepicker(false);
      let q = searchValue;

      const latestKeyword = getLatestKeyword(q.trim());
      const latestValue = latestKeyword
        ? q.substring(q.lastIndexOf(latestKeyword)).trim()
        : getLatestValue(q.trim());

      if (isTimeRangeKeyword(suggestion.name)) {
        q = `${searchValue}${getTimerangeFromEnum(suggestion.value)}`;
      } else if (
        !latestValue.toLowerCase().includes(suggestionValue.toLowerCase())
      ) {
        if (
          latestValue &&
          suggestionValue
            .toLowerCase()
            .includes(latestValue.replace(/\s+/g, "").toLowerCase())
        ) {
          // if the suggestion contains the last word the user is typing,
          const latestIdx = searchValue.lastIndexOf(latestValue);
          q = `${searchValue.substring(0, latestIdx)}${suggestionValueFixed}`;
        } else if (!q) {
          q = suggestionValueFixed;
        } else if (queryContainsSearchField(suggestionValueFixed)) {
          // if the suggestion contains a search field and is not a part of query the user is typing,
          // add it to the end of the query
          q = `${q.trim()} ${suggestionValueFixed}`;
        } else {
          // if the suggestion is a category which is a result of what the user is typing,
          // replace the last word with the suggestion
          const lastColonIndex = searchValue.lastIndexOf(":");
          const lastBracketIndex = searchValue.lastIndexOf("[");
          const latestSymbol = max([
            lastColonIndex,
            lastBracketIndex,
            searchValue.lastIndexOf(","),
            0
          ]);

          q = `${searchValue.substring(0, latestSymbol + 1)}${
            lastBracketIndex < lastColonIndex
              ? `[${suggestionValueFixed}]`
              : suggestionValueFixed
          }`;
        }
      }
      this.props.onChange(event, { newValue: q, method: "type" });
    }
  };

  renderSuggestion = ({ name, helpText, isAlias }) => {
    const searchValueContext = getLatestValue(this.props.searchValue);
    const showInlineHelp = searchValueContext.length > 0;
    return isString(name) ? (
      <div className="search__suggestion-item" title={name}>
        <span
          className={"overflow-ellipsis"}
          style={{ flex: showInlineHelp ? 0.6 : 1 }}
          data-testid={snakeCase(`search_suggestion_item_${name}`)}
        >
          {isAlias && (
            <Badge
              bg="neutral"
              className="search__suggestion-badge"
              data-testid={snakeCase(`search_suggestion_item_${name}_alias`)}
            >
              ALIAS
            </Badge>
          )}
          <StyledSubstring
            text={name}
            substring={searchValueContext}
            className="bold"
          />
        </span>
        {showInlineHelp && (
          <span
            className="search__inline-help"
            style={{ flex: 0.4 }}
            data-testid={snakeCase(
              `search_suggestion_item_${name}_helper_text`
            )}
          >
            {helpText}
          </span>
        )}
      </div>
    ) : (
      name
    );
  };

  render() {
    return (
      <AutosuggestMultiSectionExpandable
        {...this.props}
        suggestions={this.state.suggestions}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={this.renderSuggestion}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequestedDebounced}
        onSuggestionSelected={this.onSuggestionSelected}
      />
    );
  }
}

SearchAutosuggest.propTypes = {
  alwaysRenderSuggestions: PropTypes.bool,
  searchValue: PropTypes.string,
  onChange: PropTypes.func,
  showDatepicker: PropTypes.func,
  debounceValue: PropTypes.number
};

SearchAutosuggest.defaultProps = {
  alwaysRenderSuggestions: true,
  searchValue: "",
  debounceValue: 400
};

export default SearchAutosuggest;
